/**
 * MarkdownEditor — tiny, dependency-light markdown surface with a
 * simple toolbar (H2 / H3 / bold / italic / bullet / numbered / link)
 * and a live preview toggle. Text is stored as raw markdown so the
 * server can render it identically to AI-drafted narrative.
 *
 * Not a full-blown Tiptap/MDX editor — just enough to help university
 * editors produce the same look-and-feel as AI-drafted flagship prose.
 */
import { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Link as LinkIcon, Eye, Pencil,
} from "lucide-react";


function wrapSelection(el, before, after = before) {
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const val = el.value;
  const selected = val.slice(start, end) || "text";
  const next = val.slice(0, start) + before + selected + after + val.slice(end);
  return { next, cursor: start + before.length + selected.length + after.length };
}


function prefixLines(el, prefix) {
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const val = el.value;
  // Expand selection to full lines.
  const lineStart = val.lastIndexOf("\n", start - 1) + 1;
  const lineEnd = val.indexOf("\n", end);
  const boundEnd = lineEnd === -1 ? val.length : lineEnd;
  const segment = val.slice(lineStart, boundEnd);
  const patched = segment
    .split("\n")
    .map((line) => (line.startsWith(prefix) ? line : prefix + line))
    .join("\n");
  const next = val.slice(0, lineStart) + patched + val.slice(boundEnd);
  return { next, cursor: lineStart + patched.length };
}


export function MarkdownEditor({ value, onChange, testId, rows = 6, maxLength = 2500 }) {
  const [preview, setPreview] = useState(false);
  const ref = useRef(null);

  const apply = (fn) => {
    const el = ref.current;
    if (!el) return;
    const { next, cursor } = fn(el);
    onChange(next);
    // Restore focus + caret on the next tick after the value updates.
    setTimeout(() => {
      if (!ref.current) return;
      ref.current.focus();
      ref.current.setSelectionRange(cursor, cursor);
    }, 0);
  };

  const bold = () => apply((el) => wrapSelection(el, "**"));
  const italic = () => apply((el) => wrapSelection(el, "_"));
  const h2 = () => apply((el) => prefixLines(el, "## "));
  const h3 = () => apply((el) => prefixLines(el, "### "));
  const bullet = () => apply((el) => prefixLines(el, "- "));
  const numbered = () => apply((el) => prefixLines(el, "1. "));
  const link = () => {
    const url = window.prompt("URL to link to:");
    if (!url) return;
    apply((el) => wrapSelection(el, "[", `](${url})`));
  };

  const btn = "h-7 w-7 p-0";
  return (
    <div className="border border-stone-200 rounded-md bg-white" data-testid={testId}>
      <div className="flex items-center gap-0.5 border-b border-stone-200 p-1 bg-stone-50 rounded-t-md">
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={h2} title="Heading" data-testid={`${testId}-h2`}>
          <Heading2 className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={h3} title="Subheading" data-testid={`${testId}-h3`}>
          <Heading3 className="w-3.5 h-3.5" />
        </Button>
        <div className="w-px h-4 bg-stone-300 mx-1" />
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={bold} title="Bold" data-testid={`${testId}-bold`}>
          <Bold className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={italic} title="Italic" data-testid={`${testId}-italic`}>
          <Italic className="w-3.5 h-3.5" />
        </Button>
        <div className="w-px h-4 bg-stone-300 mx-1" />
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={bullet} title="Bulleted list" data-testid={`${testId}-bullet`}>
          <List className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={numbered} title="Numbered list" data-testid={`${testId}-numbered`}>
          <ListOrdered className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" size="sm" variant="ghost" className={btn} onClick={link} title="Insert link" data-testid={`${testId}-link`}>
          <LinkIcon className="w-3.5 h-3.5" />
        </Button>
        <div className="ml-auto">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-[11px]"
            onClick={() => setPreview((p) => !p)}
            data-testid={`${testId}-preview-toggle`}
            title={preview ? "Switch to editor" : "Show preview"}
          >
            {preview ? <><Pencil className="w-3 h-3 mr-1" /> Edit</> : <><Eye className="w-3 h-3 mr-1" /> Preview</>}
          </Button>
        </div>
      </div>
      {preview ? (
        <div
          className="p-3 text-sm text-stone-800 prose prose-sm max-w-none min-h-[6em]"
          data-testid={`${testId}-preview`}
        >
          {(value || "").trim() ? (
            <ReactMarkdown>{value}</ReactMarkdown>
          ) : (
            <span className="text-stone-400 italic">Nothing to preview yet.</span>
          )}
        </div>
      ) : (
        <Textarea
          ref={ref}
          rows={rows}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          className="border-0 rounded-t-none focus-visible:ring-0 font-mono text-[13px]"
          data-testid={`${testId}-textarea`}
        />
      )}
    </div>
  );
}
